import { useProviderServices } from '@/hooks/use-services';
import { Service } from '@/types/service';
import { formatCurrency } from '@/utils/format-currency';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useDebounce } from '@/hooks/use-debounce';
import { useTerms } from '@/hooks/use-terms';
import { ThemedText } from '../themed-text';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { PressableScale } from '../ui/pressable-scale';
import { SearchInput } from '../ui/search-input';
import { ErrorState } from '../ui/error-state';
import { EmptyState } from '../ui/empty-state';
import { SkeletonListRow } from '../ui/skeleton';
import { FadeInView } from '../ui/fade-in-view';
import { cn } from '@/utils/cn';

export function ServicesCard() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOption, setSearchOption] = useState('all');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  let isActiveFilter: boolean | undefined;
  if (searchOption === 'actives') {
    isActiveFilter = true;
  } else if (searchOption === 'inactives') {
    isActiveFilter = false;
  }

  const {
    data: paginatedData,
    isLoading,
    error,
    refetch,
  } = useProviderServices({
    q: debouncedSearchQuery || undefined,
    isActive: isActiveFilter,
    page: 1,
    limit: 5, // Limitar a 5 itens para preview na home
  });

  const services = paginatedData?.data || [];

  const optionsSearch = [
    { label: 'Todos', value: 'all' },
    { label: 'Ativos', value: 'actives' },
    { label: 'Inativos', value: 'inactives' },
  ];

  const handleNewService = () => {
    router.navigate('/(tabs)/(provider)/services/create');
  };

  const isMobile = Platform.OS !== 'web';
  const terms = useTerms();

  const showServiceList = services.length > 0 || debouncedSearchQuery || searchOption !== 'all';

  return (
    <Card className="w-full gap-4 p-4">
      <View className="flex-row items-center justify-between">
        <ThemedText className="font-semibold text-base text-card-foreground">
          {`${terms.agree('Meus', 'service')} ${terms.servicePlural}`}
        </ThemedText>
        <Button
          onPress={handleNewService}
          title={
            isMobile
              ? `+ ${terms.title.service}`
              : `+ ${terms.agree('Novo', 'service')} ${terms.service}`
          }
          size="xs"
          className="shrink-0"
        />
      </View>

      <View className="gap-2.5">
        <SearchInput
          placeholder={`Buscar ${terms.servicePlural}...`}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        <View className="flex-row gap-2">
          {optionsSearch.map((option) => {
            const isSelected = searchOption === option.value;
            return (
              <Pressable
                key={option.value}
                onPress={() => setSearchOption(option.value)}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                className={cn(
                  'rounded-full px-3.5 py-1.5 active:opacity-80',
                  isSelected ? 'bg-primary' : 'bg-neutral-100',
                )}
              >
                <ThemedText
                  className={cn(
                    'text-xs font-semibold',
                    isSelected ? 'text-white' : 'text-neutral-600',
                  )}
                >
                  {option.label}
                </ThemedText>
              </Pressable>
            );
          })}
        </View>
      </View>

      {isLoading ? (
        <View className="py-1">
          {Array.from({ length: 2 }).map((_, index) => (
            <SkeletonListRow key={index} />
          ))}
        </View>
      ) : error ? (
        <ErrorState
          variant="inline"
          title={`Erro ao carregar ${terms.servicePlural}`}
          message={`Não foi possível carregar ou buscar ${terms.agree('os', 'service')} ${terms.servicePlural}.`}
          onRetry={refetch}
        />
      ) : (
        <View>
          {showServiceList ? (
            <>
              {services.slice(0, 5).map((service, index) => (
                <FadeInView key={service.id} delay={index * 50}>
                  <ServiceRow service={service} />
                </FadeInView>
              ))}
              {!services.length && (
                <EmptyState
                  icon="search-outline"
                  title={`${terms.agree('Nenhum', 'service')} ${terms.service} ${terms.agree('encontrado', 'service')}`}
                  description={`Ajuste a busca ou crie ${terms.agree('um', 'service')} ${terms.agree('novo', 'service')} ${terms.service}.`}
                  actionLabel={`Criar ${terms.service}`}
                  onAction={handleNewService}
                  className="py-6"
                />
              )}
              {services.length > 0 && (
                <Pressable
                  onPress={() => router.push('/(tabs)/(provider)/services')}
                  accessibilityRole="button"
                  className="mt-1 flex-row items-center justify-between rounded-xl px-1 py-2.5 active:opacity-70"
                >
                  <ThemedText className="text-sm font-semibold text-primary">
                    {`Ver ${terms.agree('todos', 'service')} ${terms.agree('os', 'service')} ${terms.servicePlural}`}
                  </ThemedText>
                  <Ionicons name="arrow-forward" size={16} color="#FD5001" />
                </Pressable>
              )}
            </>
          ) : (
            <EmptyState
              icon="briefcase-outline"
              title={`${terms.agree('Nenhum', 'service')} ${terms.service} ${terms.agree('cadastrado', 'service')}`}
              description={`Crie ${terms.agree('seu', 'service')} ${terms.agree('primeiro', 'service')} ${terms.service} para começar a gerenciar ${terms.enrollmentPlural}.`}
              actionLabel={`Criar ${terms.service}`}
              onAction={handleNewService}
              className="py-6"
            />
          )}
        </View>
      )}
    </Card>
  );
}

const ServiceRow = ({ service }: { service: Service }) => {
  const enrollmentsCount = service?.enrollments?.length || 0;
  const terms = useTerms();

  const handleGoToService = () => {
    router.replace(`/(tabs)/(provider)/services/${service.id}`);
  };

  return (
    <PressableScale
      haptic
      onPress={handleGoToService}
      accessibilityRole="button"
      className="mb-2 w-full flex-row items-center gap-3 rounded-xl border border-neutral-900/5 bg-card px-3 py-3"
      style={{
        borderCurve: 'continuous',
        boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 4px 12px rgba(23, 17, 10, 0.05)',
      }}
    >
      <View className="h-9 w-9 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary">
        <Ionicons className="text-primary dark:text-white" name="briefcase-outline" size={16} />
      </View>
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-sm text-card-foreground" numberOfLines={1}>
          {service.name}
        </ThemedText>
        <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
          {service.defaultPrice ? formatCurrency(service.defaultPrice) : 'Valor a combinar'} ·{' '}
          {enrollmentsCount} {enrollmentsCount === 1 ? terms.enrollment : terms.enrollmentPlural}
        </ThemedText>
      </View>
      <Ionicons className="text-neutral-300" name="chevron-forward" size={16} />
    </PressableScale>
  );
};
